<template>
  <div>
    <!-- Floating toggle button -->
    <v-btn
      v-if="enabled && !open"
      class="debug-fab"
      color="secondary"
      icon
      @click="toggleOpen(true)"
      :title="'Open Debug Widget'"
    >
      <v-icon>mdi-bug-outline</v-icon>
    </v-btn>

    <!-- Bottom debug panel -->
    <div v-if="enabled && open" class="debug-panel">
      <div class="debug-header">
        <div class="left">
          <div class="title d-flex align-center ga-2">
            <v-icon>mdi-bug-outline</v-icon>
            <span class="text-subtitle-1">Debug View</span>
            <v-chip size="x-small" variant="outlined">{{ envLabel }}</v-chip>
            <v-chip size="x-small" variant="outlined">{{ routeName }}</v-chip>
          </div>
          <v-tabs v-model="tab" density="compact" class="mt-1">
            <v-tab value="events">Realtime</v-tab>
            <v-tab value="console">Console</v-tab>
          </v-tabs>
        </div>
        <div class="right d-flex align-center ga-2">
          <v-chip size="x-small" variant="outlined" class="mr-2">{{ transportLabel }}</v-chip>
          <v-btn size="small" variant="text" icon :title="activeTabIsConsole ? 'Copy console' : 'Copy events'" @click="copyActive">
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
          <v-btn size="small" variant="text" icon :title="activeTabIsConsole ? 'Clear console' : 'Clear events'" @click="clearActive">
            <v-icon>mdi-broom</v-icon>
          </v-btn>
          <v-btn size="small" variant="text" icon :title="'Minimize'" @click="toggleOpen(false)">
            <v-icon>mdi-window-minimize</v-icon>
          </v-btn>
        </div>
      </div>
      <div class="debug-body">
        <v-tabs-window v-model="tab" class="fill-height">
          <v-tabs-window-item value="events" class="fill-height">
            <div v-if="logs.length === 0" class="pa-4 text-medium-emphasis">
              No debug logs yet. Interact with the app to see events.
            </div>
            <div class="log-container" ref="listRef">
              <div 
                v-for="entry in logs" 
                :key="entry.id" 
                class="log-item" 
                :class="`lvl-${entry.level}`"
                @dblclick="showLogDetails(entry)"
                :title="'Double-click to view details'"
              >
                <div class="log-line">
                  <span class="ts">{{ formatTs(entry.ts) }}</span>
                  <span class="lvl">{{ entry.level.toUpperCase() }}</span>
                  <span class="src">{{ entry.source }}</span>
                  <span class="msg">{{ entry.message }}</span>
                  <span v-if="entry.payload" class="payload-indicator">{{ formatPayloadPreview(entry.payload) }}</span>
                </div>
              </div>
            </div>
          </v-tabs-window-item>
          <v-tabs-window-item value="console" class="fill-height">
            <div v-if="consoleEntries.length === 0" class="pa-4 text-medium-emphasis">
              No console output captured.
            </div>
            <div class="log-container" ref="consoleListRef">
              <div 
                v-for="c in consoleEntries" 
                :key="c.id" 
                class="log-item" 
                :class="`lvl-${c.level}`"
                @dblclick="showConsoleDetails(c)"
                :title="'Double-click to view details'"
              >
                <div class="log-line">
                  <span class="ts">{{ formatTs(c.ts) }}</span>
                  <span class="lvl">{{ c.level.toUpperCase() }}</span>
                  <span class="console-msg">{{ formatArgsPreview(c.args) }}</span>
                </div>
              </div>
            </div>
          </v-tabs-window-item>
        </v-tabs-window>
      </div>
    </div>

    <!-- Log Details Dialog -->
    <LogDetailsDialog 
      v-if="selectedLogEntry"
      v-model="showDetailsDialog" 
      :log-entry="selectedLogEntry" 
    />

    <!-- Console Details Dialog -->
    <ConsoleDetailsDialog 
      v-if="selectedConsoleEntry"
      v-model="showConsoleDetailsDialog" 
      :console-entry="selectedConsoleEntry" 
    />
  </div>
</template>

<script setup lang="ts">
import LogDetailsDialog from '@/components/debug/LogDetailsDialog.vue'
import ConsoleDetailsDialog from '@/components/debug/ConsoleDetailsDialog.vue'
import { useDebugWidget } from '@/composables/useDebugWidget'

const {
  enabled,
  open,
  logs,
  consoleEntries,
  tab,
  listRef,
  consoleListRef,
  showDetailsDialog,
  showConsoleDetailsDialog,
  selectedLogEntry,
  selectedConsoleEntry,
  routeName,
  envLabel,
  transportLabel,
  activeTabIsConsole,
  toggleOpen,
  copyActive,
  clearActive,
  showLogDetails,
  showConsoleDetails,
  formatTs,
  formatPayloadPreview,
  formatArgsPreview,
} = useDebugWidget()
</script>

<style scoped>
.debug-fab {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 9999;
}

.debug-panel {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 40vh;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(0,0,0,0.12);
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.debug-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(0,0,0,0.12);
  flex-shrink: 0;
}

.debug-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.fill-height {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.log-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
}

.log-item {
  padding: 4px 8px;
  border-radius: 4px;
  margin-bottom: 2px;
  border: 1px solid rgba(0,0,0,0.06);
  cursor: pointer;
  transition: background-color 0.1s ease;
}

.log-item:hover {
  background-color: rgba(0,0,0,0.04);
}

.log-line {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.log-item .ts { 
  color: var(--v-theme-primary); 
  font-weight: 600; 
  flex-shrink: 0;
  min-width: 70px;
}

.log-item .lvl { 
  opacity: 0.8; 
  flex-shrink: 0;
  min-width: 45px;
}

.log-item .src { 
  opacity: 0.8; 
  flex-shrink: 0;
  min-width: 30px;
}

.log-item .msg { 
  font-weight: 600; 
  flex-shrink: 1;
  min-width: 0;
}

.log-item .payload-indicator,
.log-item .console-msg { 
  opacity: 0.7;
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.log-item.lvl-warn { 
  background: rgba(255, 165, 0, 0.08); 
  border-color: rgba(255, 165, 0, 0.2);
}

.log-item.lvl-error { 
  background: rgba(255, 0, 0, 0.08); 
  border-color: rgba(255, 0, 0, 0.2);
}

.log-item.lvl-event { 
  background: rgba(0, 128, 255, 0.06); 
  border-color: rgba(0, 128, 255, 0.2);
}
</style>
